import {useMemo} from "react";
import {connect} from "react-redux";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {Checkbox} from "@pg-design/checkbox-module";
import {display, flexDirection, ml, pl, position, pr} from "@pg-design/helpers-css";
import {themeRP} from "@pg-design/themes";
import {form, IFormProps} from "@pg-mono/form";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {PreferencesAction} from "../../analytics/events";
import {IPropertyConfiguratorSearchFiltersFormValues} from "../../reducers/property_configurator_reducer";
import {trackCheckbox} from "../../utils/track_checkbox";
import {IStateProps} from "../../views/PropertyConfigurator";

interface IOwnProps {
    quarterGap?: number;
    labelOffset?: number;
}

interface IProps extends IFormProps<IPropertyConfiguratorSearchFiltersFormValues>, IStateProps, IOwnProps {}

export const WorldDirectionFilterC = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const {propertyQuarters, quarterGap = 12 * (isMobile ? 0.6 : 1)} = props;
    const worldDirections = props.getFieldProps("world_directions");
    const sortList = ["Północ", "Wschód", "Południe", "Zachód"];

    const orderedQuarters = useMemo(
        () => [...(propertyQuarters || [])]?.sort((a, b) => sortList.indexOf(a.label) - sortList.indexOf(b.label)),
        [propertyQuarters]
    );

    const onOptionSelect = (name: string) => {
        const selectedOptionValue = propertyQuarters?.find((el) => el.label === name)?.value.toString();
        const currentValue = worldDirections.value || [];
        const valueSelected = currentValue.includes(selectedOptionValue as string);

        if (!valueSelected) {
            trackCheckbox(true, name, PreferencesAction.WORLD_DIRECTION);
            return worldDirections.onChange(worldDirections.name, [...currentValue, (selectedOptionValue as string)?.toString()]);
        }

        trackCheckbox(false, name, PreferencesAction.WORLD_DIRECTION);
        const filtered = currentValue.filter((worldDirectionChoiceValue: string) => worldDirectionChoiceValue !== selectedOptionValue);
        worldDirections.onChange(worldDirections.name, [...filtered]);
    };
    return (
        <Wrapper>
            {orderedQuarters?.map((directionOption, index) => (
                <Quarter
                    css={quarterStyles(quarterGap)[index]}
                    onClick={() => onOptionSelect(directionOption.label)}
                    key={directionOption.value}
                    checked={worldDirections.value.includes(directionOption.value.toString())}
                    quarterGap={quarterGap}
                />
            ))}
            {orderedQuarters?.map((directionOption, index) => (
                <Checkbox
                    id={"direction_option_" + directionOption.value}
                    labelContent={directionOption.label}
                    name={directionOption.label}
                    checked={worldDirections.value.includes(directionOption.value.toString())}
                    onChange={onOptionSelect}
                    css={[position("absolute"), checkboxStyles(props.labelOffset)[index]]}
                    key={directionOption.value}
                />
            ))}
        </Wrapper>
    );
};

export const WorldDirectionFilter = connect(mapStateToProps)(form<IPropertyConfiguratorSearchFiltersFormValues, IProps>(WorldDirectionFilterC));

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 400px;
    max-height: 400px;
    aspect-ratio: 1 / 1;
    overflow: hidden;
`;

interface QuarterProps {
    checked: boolean;
    quarterGap?: number;
}

const Quarter = styled.div<QuarterProps>`
    ${display("block")};
    width: calc(50% - ${({quarterGap}) => quarterGap}px);
    height: calc(50% - ${({quarterGap}) => quarterGap}px);
    ${position("absolute")};
    background: ${({theme, checked}) => (checked ? theme.colors.primary : "#f9f9f9")};
    ${({checked, theme}) =>
        checked &&
        css`
            border: 1px solid ${theme.colors.secondary};
        `}
    box-sizing: border-box;
    transform: rotate(45deg);
    cursor: pointer;
`;

const quarterStyles = (quarterGap: number = 12) => [
    css`
        border-radius: 100% 0 0 0;
        left: ${quarterGap}px;
        top: 0;
        transform-origin: bottom right;
    `,
    css`
        border-radius: 0 100% 0 0;
        top: ${quarterGap}px;
        right: 0;
        transform-origin: bottom left;
    `,
    css`
        border-radius: 0 0 100% 0;
        bottom: 0;
        right: ${quarterGap}px;
        transform-origin: top left;
    `,
    css`
        border-radius: 0 0 0 100%;
        left: 0;
        bottom: ${quarterGap}px;
        transform-origin: top right;
    `
];

const checkboxStyles = (labelOffset: number = 70) => [
    css`
        ${flexDirection("column")};
        left: 50%;
        transform: translate(-50%, -100%);
        top: calc(50% - ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            top: calc(50% - ${labelOffset}px);
        }

        > div {
            width: 100%;
        }
    `,
    css`
        ${flexDirection("row-reverse")};
        transform: translate(0%, -50%);
        top: calc(50%);
        left: calc(50% + ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            left: calc(50% + ${labelOffset}px);
        }

        > label {
            ${ml(0)};
            ${pr(0.5)};
        }
    `,
    css`
        ${flexDirection("column-reverse")};
        left: 50%;
        transform: translate(-50%, 100%);
        bottom: calc(50% - ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            bottom: calc(50% - ${labelOffset}px);
        }

        > div {
            width: 100%;
        }
    `,
    css`
        ${flexDirection("row")};
        transform: translate(0%, -50%);
        top: calc(50%);
        right: calc(50% + ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            right: calc(50% + ${labelOffset}px);
        }

        > label {
            ${ml(0)};
            ${pl(0.5)}
        }
    `
];
import {useMemo} from "react";
import {connect} from "react-redux";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {Checkbox} from "@pg-design/checkbox-module";
import {display, flexDirection, ml, pl, position, pr} from "@pg-design/helpers-css";
import {themeRP} from "@pg-design/themes";
import {form, IFormProps} from "@pg-mono/form";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {PreferencesAction} from "../../analytics/events";
import {IPropertyConfiguratorSearchFiltersFormValues} from "../../reducers/property_configurator_reducer";
import {trackCheckbox} from "../../utils/track_checkbox";
import {IStateProps} from "../../views/PropertyConfigurator";

interface IOwnProps {
    quarterGap?: number;
    labelOffset?: number;
}

interface IProps extends IFormProps<IPropertyConfiguratorSearchFiltersFormValues>, IStateProps, IOwnProps {}

export const WorldDirectionFilterC = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const {propertyQuarters, quarterGap = 12 * (isMobile ? 0.6 : 1)} = props;
    const worldDirections = props.getFieldProps("world_directions");
    const sortList = ["Północ", "Wschód", "Południe", "Zachód"];

    const orderedQuarters = useMemo(
        () => [...(propertyQuarters || [])]?.sort((a, b) => sortList.indexOf(a.label) - sortList.indexOf(b.label)),
        [propertyQuarters]
    );

    const onOptionSelect = (name: string) => {
        const selectedOptionValue = propertyQuarters?.find((el) => el.label === name)?.value.toString();
        const currentValue = worldDirections.value || [];
        const valueSelected = currentValue.includes(selectedOptionValue as string);

        if (!valueSelected) {
            trackCheckbox(true, name, PreferencesAction.WORLD_DIRECTION);
            return worldDirections.onChange(worldDirections.name, [...currentValue, (selectedOptionValue as string)?.toString()]);
        }

        trackCheckbox(false, name, PreferencesAction.WORLD_DIRECTION);
        const filtered = currentValue.filter((worldDirectionChoiceValue: string) => worldDirectionChoiceValue !== selectedOptionValue);
        worldDirections.onChange(worldDirections.name, [...filtered]);
    };
    return (
        <Wrapper>
            {orderedQuarters?.map((directionOption, index) => (
                <Quarter
                    css={quarterStyles(quarterGap)[index]}
                    onClick={() => onOptionSelect(directionOption.label)}
                    key={directionOption.value}
                    checked={worldDirections.value.includes(directionOption.value.toString())}
                    quarterGap={quarterGap}
                />
            ))}
            {orderedQuarters?.map((directionOption, index) => (
                <Checkbox
                    id={"direction_option_" + directionOption.value}
                    labelContent={directionOption.label}
                    name={directionOption.label}
                    checked={worldDirections.value.includes(directionOption.value.toString())}
                    onChange={onOptionSelect}
                    css={[position("absolute"), checkboxStyles(props.labelOffset)[index]]}
                    key={directionOption.value}
                />
            ))}
        </Wrapper>
    );
};

export const WorldDirectionFilter = connect(mapStateToProps)(form<IPropertyConfiguratorSearchFiltersFormValues, IProps>(WorldDirectionFilterC));

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 400px;
    max-height: 400px;
    aspect-ratio: 1 / 1;
    overflow: hidden;
`;

interface QuarterProps {
    checked: boolean;
    quarterGap?: number;
}

const Quarter = styled.div<QuarterProps>`
    ${display("block")};
    width: calc(50% - ${({quarterGap}) => quarterGap}px);
    height: calc(50% - ${({quarterGap}) => quarterGap}px);
    ${position("absolute")};
    background: ${({theme, checked}) => (checked ? theme.colors.primary : "#f9f9f9")};
    ${({checked, theme}) =>
        checked &&
        css`
            border: 1px solid ${theme.colors.secondary};
        `}
    box-sizing: border-box;
    transform: rotate(45deg);
    cursor: pointer;
`;

const quarterStyles = (quarterGap: number = 12) => [
    css`
        border-radius: 100% 0 0 0;
        left: ${quarterGap}px;
        top: 0;
        transform-origin: bottom right;
    `,
    css`
        border-radius: 0 100% 0 0;
        top: ${quarterGap}px;
        right: 0;
        transform-origin: bottom left;
    `,
    css`
        border-radius: 0 0 100% 0;
        bottom: 0;
        right: ${quarterGap}px;
        transform-origin: top left;
    `,
    css`
        border-radius: 0 0 0 100%;
        left: 0;
        bottom: ${quarterGap}px;
        transform-origin: top right;
    `
];

const checkboxStyles = (labelOffset: number = 70) => [
    css`
        ${flexDirection("column")};
        left: 50%;
        transform: translate(-50%, -100%);
        top: calc(50% - ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            top: calc(50% - ${labelOffset}px);
        }

        > div {
            width: 100%;
        }
    `,
    css`
        ${flexDirection("row-reverse")};
        transform: translate(0%, -50%);
        top: calc(50%);
        left: calc(50% + ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            left: calc(50% + ${labelOffset}px);
        }

        > label {
            ${ml(0)};
            ${pr(0.5)};
        }
    `,
    css`
        ${flexDirection("column-reverse")};
        left: 50%;
        transform: translate(-50%, 100%);
        bottom: calc(50% - ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            bottom: calc(50% - ${labelOffset}px);
        }

        > div {
            width: 100%;
        }
    `,
    css`
        ${flexDirection("row")};
        transform: translate(0%, -50%);
        top: calc(50%);
        right: calc(50% + ${labelOffset - 15}px);
        @media (min-width: ${themeRP.breakpoints.xs}) {
            right: calc(50% + ${labelOffset}px);
        }

        > label {
            ${ml(0)};
            ${pl(0.5)}
        }
    `
];
